import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CalendarClock, Play, Trash2, Plus, CopyX, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const INTERVALS = [
  { value: 15, label: "Every 15 min" },
  { value: 30, label: "Every 30 min" },
  { value: 60, label: "Hourly" },
  { value: 360, label: "Every 6 h" },
  { value: 720, label: "Every 12 h" },
  { value: 1440, label: "Daily" },
];

const JOB_TONE = {
  done: "bg-teal-50 border-teal-200 text-teal-700",
  duplicate: "bg-amber-50 border-amber-200 text-amber-700",
  failed: "bg-red-50 border-red-200 text-red-700",
  queued: "bg-slate-50 border-slate-200 text-slate-500",
  running: "bg-blue-50 border-blue-200 text-blue-700",
};

function JobStatus({ status }) {
  const Icon = { done: CheckCircle2, duplicate: CopyX, failed: XCircle, running: Loader2 }[status];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${JOB_TONE[status] || JOB_TONE.queued}`}>
      {Icon && <Icon className={`h-3 w-3 ${status === "running" ? "animate-spin" : ""}`} />}
      {status}
    </span>
  );
}

export function ScheduledSources({ readOnly }) {
  const [sources, setSources] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [url, setUrl] = useState("");
  const [locality, setLocality] = useState("");
  const [interval, setIntervalMin] = useState(60);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, j] = await Promise.all([api.get("/crawl/sources"), api.get("/crawl/jobs?limit=15")]);
      setSources(s.data);
      setJobs(j.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 20000);
    return () => clearInterval(id);
  }, [refresh]);

  const addSource = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/crawl/sources", { url: url.trim(), locality: locality.trim(), interval_minutes: interval });
      toast.success("Recurring crawl scheduled — first run within 30 seconds");
      setUrl(""); setLocality("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to schedule");
    } finally { setBusy(false); }
  };

  const toggleActive = async (s) => {
    await api.patch(`/crawl/sources/${s.id}`, { active: !s.active });
    toast.success(s.active ? "Source paused" : "Source resumed");
    refresh();
  };

  const runNow = async (s) => {
    try {
      await api.post(`/crawl/sources/${s.id}/run`);
      toast.success("Crawl queued — runs within 30 seconds");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not queue crawl");
    }
  };

  const remove = async (s) => {
    await api.delete(`/crawl/sources/${s.id}`);
    toast.success("Source removed");
    refresh();
  };

  return (
    <section className="border border-slate-200 bg-white rounded-md p-5 max-w-3xl space-y-4" data-testid="scheduled-sources">
      <div>
        <h2 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-blue-600" /> Recurring crawls
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Sources are re-crawled automatically on a schedule. Pages whose content is identical
          to an existing advertisement are skipped (marked <span className="font-mono">duplicate</span>).
        </p>
      </div>

      {!readOnly && (
        <form onSubmit={addSource} className="grid sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
          <div className="sm:col-span-4 grid sm:grid-cols-2 gap-2">
            <Input data-testid="source-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://public-classifieds.example.com/bangalore" required />
            <Input data-testid="source-locality-input" value={locality} onChange={(e) => setLocality(e.target.value)}
              placeholder="Locality (optional)" />
          </div>
          <select data-testid="source-interval-select" value={interval} onChange={(e) => setIntervalMin(Number(e.target.value))}
            className="border border-slate-300 rounded-md text-sm px-2 py-2 h-9">
            {INTERVALS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
          </select>
          <Button type="submit" disabled={busy} data-testid="source-add-btn" className="bg-slate-900 hover:bg-slate-800 gap-1.5 h-9 sm:col-span-3">
            <Plus className="h-4 w-4" /> Schedule source
          </Button>
        </form>
      )}

      <ul className="divide-y divide-slate-100">
        {sources.map((s) => (
          <li key={s.id} data-testid={`source-row-${s.id}`} className="py-2.5 flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-mono truncate">{s.url}</div>
              <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                {INTERVALS.find((i) => i.value === s.interval_minutes)?.label || `${s.interval_minutes} min`}
                {s.locality && <> · {s.locality}</>}
                {" · "}{s.run_count || 0} runs · {s.ingested_count || 0} ingested · {s.duplicate_count || 0} dupes
                {s.last_status && <> · last: {s.last_status}</>}
              </div>
            </div>
            {!readOnly && (
              <div className="flex items-center gap-2">
                <Switch data-testid={`source-active-${s.id}`} checked={!!s.active} onCheckedChange={() => toggleActive(s)} />
                <Button size="sm" variant="outline" className="h-8 border-slate-300 gap-1" data-testid={`source-run-${s.id}`} onClick={() => runNow(s)}>
                  <Play className="h-3.5 w-3.5" /> Run now
                </Button>
                <Button size="sm" variant="outline" className="h-8 border-slate-300 text-red-600 hover:text-red-700" data-testid={`source-delete-${s.id}`} onClick={() => remove(s)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </li>
        ))}
        {sources.length === 0 && <li className="text-xs text-slate-400 py-2">No recurring sources scheduled.</li>}
      </ul>

      <div>
        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2">Recent jobs</h3>
        <ul className="space-y-1.5" data-testid="crawl-jobs-list">
          {jobs.map((j) => (
            <li key={j.id} className="flex items-center gap-2 text-xs">
              <JobStatus status={j.status} />
              <span className="font-mono text-slate-500 truncate flex-1">{j.url}</span>
              {j.status === "duplicate" && j.ad_id && (
                <Link to={`/ads/${j.ad_id}`} className="text-amber-700 hover:underline whitespace-nowrap">matches existing ad</Link>
              )}
              {j.status === "done" && j.ad_id && (
                <Link to={`/ads/${j.ad_id}`} className="text-blue-700 hover:underline whitespace-nowrap">open ad</Link>
              )}
              {j.status === "failed" && <span className="text-red-600 truncate max-w-[180px]" title={j.error}>{j.error}</span>}
              <span className="text-slate-400 font-mono whitespace-nowrap">{(j.created_at || "").slice(5, 16).replace("T", " ")}</span>
            </li>
          ))}
          {jobs.length === 0 && <li className="text-xs text-slate-400">No jobs yet.</li>}
        </ul>
      </div>
    </section>
  );
}
